import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Building2, Mail, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import { useAuth } from "../AuthContext";
import { adminHttp, formatApiError } from "../../lib/adminApi";

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const change = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Las contraseñas no coinciden");
    if (form.new_password.length < 8) return toast.error("Mínimo 8 caracteres");
    setLoading(true);
    try {
      await adminHttp.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8" data-testid="admin-settings-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <span className="divider-accent" /><span className="kicker">Configuración</span>
        </div>
        <h1 className="font-display font-black tracking-tightest text-3xl md:text-5xl">Ajustes</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DDE2D5] p-6 md:p-8">
          <h3 className="font-display font-black text-xl mb-5 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-accent" /> Cambiar mi contraseña
          </h3>
          <form onSubmit={change} className="space-y-4">
            <Input label="Contraseña actual" type="password" value={form.current_password} onChange={(v) => setForm({ ...form, current_password: v })} required />
            <Input label="Nueva contraseña" type="password" value={form.new_password} onChange={(v) => setForm({ ...form, new_password: v })} required />
            <Input label="Confirmar nueva contraseña" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} required />
            <button type="submit" disabled={loading} className="btn-accent justify-center disabled:opacity-60" data-testid="settings-change-password">
              {loading ? "Actualizando…" : "Actualizar contraseña"}
            </button>
          </form>
        </div>

        <div className="bg-white border border-[#DDE2D5] p-6 md:p-8">
          <h3 className="font-display font-black text-xl mb-5 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-accent" /> Empresa
          </h3>
          <ul className="space-y-4 text-sm text-[#374151]">
            <li className="flex items-start gap-3">
              <Building2 className="w-4 h-4 text-accent mt-0.5" />
              <div>
                <p className="font-display font-bold text-[#1F2937]">Eclipse Cubiertas SA de CV</p>
                <p className="text-[#4B5563] text-xs">Marca comercial: Velarias Tens</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-accent mt-0.5" />
              <span><span className="text-[#6B7280] text-xs uppercase tracking-wider mr-2">Llamadas:</span>+52 81 8989 9822</span>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle className="w-4 h-4 text-[#25D366] mt-0.5" />
              <span><span className="text-[#6B7280] text-xs uppercase tracking-wider mr-2">WhatsApp:</span>+52 81 1628 9267</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-accent mt-0.5" /><span>ventas@velariastens.com.mx</span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-accent mt-0.5" /><span>Calle San Pedro 157, Bodega 8, Extora Park Flex Space, Col. 4, CP 66367, Sta. Catarina, N.L.</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-accent mt-0.5" /><span className="font-mono text-xs">Lun-Vie 8am-8pm · Sáb 9am-2pm · Dom cerrado</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-navy-600 border border-accent/30 p-6 md:p-8">
        <h3 className="font-display font-black text-xl mb-3 text-white">Configuración avanzada</h3>
        <p className="text-white/85 text-sm">
          Integraciones de email marketing (Resend), analítica extendida (funnel + cohorts) y Google Ads Conversion API disponibles en las Fases 3-5 del roadmap.
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/70">
          Sesión actual: {user?.email} · {user?.role}
        </p>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
        className="mt-2 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] focus:outline-none focus:border-accent"
      />
    </label>
  );
}
